import { MessageCircle, CalendarDays, X } from 'lucide-react';
import { ChatHistoryProps } from '@/types/componentsTypes';
import { useEffect, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useGetUserConversations } from '@/hooks/useAuth';
import { Conversation } from '@/types/conversationTypes';

export function ChatHistory({ onClose, onSelectConversation }: ChatHistoryProps) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const getUserConversations = useGetUserConversations();
  const { user } = useCurrentUser();

  useEffect(() => {
    if (user?.id) {
      getUserConversations.mutate(user.id, {
        onSuccess: (data) => {
          setConversations(data);
        },
      });
    }
  }, [user?.id]);

  return (
    <div className="bg-white rounded-2xl shadow-xl flex flex-col h-full overflow-hidden">
      <div className="bg-brand-600 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-white" />
          <h2 className="font-semibold text-sm">Conversation History</h2>
        </div>
        <button onClick={onClose}>
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="flex-1 p-4 overflow-y-auto">
        {!conversations || getUserConversations.isPending ? (
          <div>
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="border border-gray-200 rounded-lg p-4 mb-4 animate-pulse"
              >
                <div className="h-4 w-32 bg-gray-200 rounded" />
                <div className="flex items-center justify-between mt-4">
                  <div className="h-3 w-20 bg-gray-200 rounded" />
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-16 bg-gray-200 rounded" />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <div className="h-3 w-28 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No previous conversations</p>
          </div>
        ) : (
          conversations.map((conversation) => (
            <div
              key={conversation.id}
              className="border border-gray-200 rounded-lg p-4 mb-4 hover:shadow-sm transition cursor-pointer"
              onClick={() => onSelectConversation(conversation.id)}
            >
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm flex-1 truncate">{conversation.title || "Trip planning"}</h3>
                {conversation.status === 'active' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold uppercase tracking-wide">
                    Active
                  </span>
                )}
                {conversation.status === 'closed' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-semibold uppercase tracking-wide">
                    Closed
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                <div>{conversation.messages.length} messages</div>
                <div className="flex items-center gap-1">
                  <CalendarDays className="w-4 h-4" />
                  <span>{new Date(conversation.updatedAt).toLocaleString('ro-RO')}</span>
                </div>
              </div>
              <div className="mt-2 text-right">
                <button
                  className="text-brand-600 text-sm font-medium hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectConversation(conversation.id);
                  }}
                >
                  View conversation →
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="border-t p-4">
        <button
          onClick={onClose}
          className="block w-full text-center text-sm font-medium text-brand-600 hover:underline"
        >
          Back to Current Chat
        </button>
      </div>
    </div>
  );
}